import { useSuspenseQuery } from '@tanstack/react-query';

import type * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    type OverlayTriggerState,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { createDocumentationURL } from '../../utils';
import { OpenAPIAddSpecButton } from './OpenAPIAddSpecButton';

/**
 * Empty state displayed when there are no OpenAPI specs.
 */
export function OpenAPIEmptyState(props: {
    organizationId: string;
    addOpenAPISpecTrigger: OverlayTriggerState;
    className?: string;
    analyticsContext: analytics.OpenAPIAnalyticsCreateContext;
}) {
    const { organizationId, className, analyticsContext } = props;
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    return (
        <EmptyState className={className}>
            <EmptyStateIcon>
                <Icon.OpenAPI />
            </EmptyStateIcon>
            <Heading>Generate API docs automatically</Heading>
            {organization.permissions.createOpenAPISpec ? (
                <>
                    <Description>
                        Upload or link your OpenAPI specification to instantly generate API
                        documentation without writing a word.
                    </Description>
                    <EmptyStateActions>
                        <Button
                            label="Read the docs"
                            kind="secondary"
                            href={createDocumentationURL('api-references/openapi')}
                            target="_blank"
                        />
                        <OpenAPIAddSpecButton
                            organizationId={organizationId}
                            trigger={props.addOpenAPISpecTrigger}
                            analyticsContext={analyticsContext}
                        />
                    </EmptyStateActions>
                </>
            ) : (
                <Description>
                    You don't seem to have permission to add a specification. Reach out to your
                    organization's admin to get started.
                </Description>
            )}
        </EmptyState>
    );
}
